import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useClickOutside from "../TransactionHistory/useClickOutside";
import { ChevronDownIcon, InfoCircleIcon } from "../../icons";
import ChevronIcon from "../../icons/ChevronIcon";
import PlusIcon from "../../icons/PlusIcon";
// import { BeneficiarySkeleton, Skeleton } from "../../../components/Skeleton/CryptoBeneficiariesSkeleton";
import { scrollIntoViewWithEase } from "../../../helper/ScrollHelper";
import Avatar from "./Avatar";
import { BeneficiarySkeleton } from "../../../components/Skeleton/CryptoBeneficiariesSkeleton";
import Skeleton from "../../../components/Common/Skeleton";

const getOptionLabel = (option, labelKey) => {
  if (option == null) return "";
  if (typeof option === "object") return option[labelKey] ?? "";
  return option;
};

const getOptionValue = (option, valueKey) => {
  if (option == null) return "";
  if (typeof option === "object") return option[valueKey];
  return option;
};

const isSameOption = (option, value, valueKey) => {
  if (!value) return false;

  return getOptionValue(option, valueKey) === getOptionValue(value, valueKey);
};

export const handleDropdownEnter = (event, filteredItems, onSelect) => {
  if (event.key !== "Enter") return;

  event.preventDefault();

  const firstItem = filteredItems?.[0];

  if (firstItem) {
    onSelect(firstItem);
  }
};


const FieldLabel = ({ children, showInfo = false }) => (
  <div className="flex items-center gap-1">
    <p className="text-sm font-medium leading-[21px] text-text-primary">{children}</p>
    {showInfo && <InfoCircleIcon className="size-3 shrink-0 text-text-secondary" />}
  </div>
);

const fieldShellClass =
  "flex h-12 w-full items-center justify-between overflow-hidden rounded-[10px] border border-solid border-divider bg-background-surface pr-3 py-2.5";

const dropdownMenuClass =
  "absolute left-0 right-0 top-[calc(100%+4px)] z-50 overflow-hidden rounded-[8px] bg-background-surface px-3 pb-3 pt-[13px] shadow-[0px_8px_16px_0px_rgba(0,0,0,0.16)] custom-scrollbar";


export const FormSelect = ({
  label,
  value,
  placeholder = "Select",
  options = [],
  onChange,

  // customization
  labelKey = "label",
  valueKey = "id",
  iconKey = null,

  showInfo,
  showChevron = true,
  filled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useClickOutside(containerRef, isOpen, () => setIsOpen(false));

  const displayValue = getOptionLabel(value, labelKey);
  const hasValue = Boolean(displayValue);
  const showFilledStyle = filled || hasValue;

  return (
    <div ref={containerRef} className="relative flex w-full flex-col gap-1">
      <FieldLabel showInfo={showInfo}>{label}</FieldLabel>

      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className={`${fieldShellClass} text-left custom-transition hover:bg-option-hover`}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {value?.[iconKey] && (
            <img
              src={value[iconKey]}
              alt=""
              className="h-5 w-5 rounded-full object-contain"
            />
          )}

          <span
            className={`truncate text-sm leading-normal ${
              showFilledStyle
                ? "font-medium text-text-primary"
                : "font-normal text-text-secondary"
            }`}
          >
            {displayValue || placeholder}
          </span>
        </div>

        {showChevron ? (
          <ChevronDownIcon
            size={20}
            className={`shrink-0 text-text-secondary custom-transition ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        ) : (
          <span className="size-4 shrink-0" />
        )}
      </button>

      {isOpen && (
        <div className={dropdownMenuClass}>
          <div className="flex flex-col gap-1">
            {options.map((option) => {
              const optionLabel = getOptionLabel(option, labelKey);
              const optionValue = getOptionValue(option, valueKey);
              const isSelected = isSameOption(option, value, valueKey);

              return (
                <button
                  key={optionValue}
                  type="button"
                  onClick={() => {
                    onChange?.(option);
                    setIsOpen(false);
                  }}
                  className={`flex h-10 w-full items-center gap-2 rounded-[4px] px-[14px] text-left custom-transition ${
                    isSelected
                      ? "bg-[rgba(74,185,50,0.1)]"
                      : "hover:bg-[rgba(74,185,50,0.06)]"
                  }`}
                >
                  {iconKey && option[iconKey] && (
                    <img
                      src={option[iconKey]}
                      alt=""
                      className="h-5 w-5 rounded-full object-contain"
                    />
                  )}

                  <span className="text-xs font-medium leading-normal text-text-primary">
                    {optionLabel}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};


// ----------------------------------------------------------------------------------------------------------


const CryptoAccountItem = ({ account, selected = false, compact = false, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left custom-transition ${
      selected
        ? "bg-[rgba(74,185,50,0.10)]"
        : "hover:bg-[rgba(74,185,50,0.06)] bg-background-alt"
    }`}
  >
    <img
      src={account.logo}
      alt={account.currency_code}
      className="h-10 w-10 shrink-0 rounded-full object-contain"
    />

    <div className="min-w-0 flex-1">
      <div className="flex items-center justify-between gap-3">
        <p className="truncate text-sm font-semibold text-text-primary">
          {account.currency_code}
        </p>
      </div>

      <p className="truncate text-xs text-text-primary">
        {account.title}
      </p>

      {/* {!compact && (
        <>
          <p className="mt-0.5 text-[11px] text-text-secondary">
            #{account.account_number}
          </p>
        </>
      )} */}
    </div>
  </button>
);

const CryptoAccountSkeleton = () => (
  <div className="animate-pulse rounded-lg bg-background-alt p-4">
    <div className="flex items-center gap-3">
      <Skeleton className="h-10 w-10 rounded-full" />

      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-32 rounded" />
        <Skeleton className="h-3 w-20 rounded" />
      </div>
    </div>
  </div>
);

export const CryptoAccountSelectDropdown = ({
  label,
  accounts = [],
  value,
  onChange,
  placeholder = "Select crypto account",
  showInfo = false,
  loading = false,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");

  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const closeDropdown = () => {
    setIsOpen(false);
    setSearch("");
  };

  useClickOutside(containerRef, isOpen, closeDropdown);

  const filteredAccounts = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return accounts;
    
    return accounts.filter((account) =>
      [
        account?.title,
        // account?.account_number,
        account?.currency_code,
        // account?.label,
        // account?.wallet_address,
      ]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(query))
    );
  }, [accounts, search]);

  const handleSelect = (account) => {
    onChange?.(account);
    closeDropdown();
  };

  return (
    <div
      ref={containerRef}
      className="relative flex w-full flex-col gap-3"
    >
      <FieldLabel showInfo={showInfo}>
        {label}
      </FieldLabel>

      <div className="relative h-12">
        {/* Closed State */}
        <button
          type="button"
          disabled={disabled}
          onClick={() => setIsOpen(true)}
          className={`absolute inset-0 flex min-h-12 w-full items-center rounded-[10px] border border-divider bg-background-surface text-left custom-transition duration-200 ${
            isOpen
              ? "pointer-events-none scale-[0.98] opacity-0"
              : "scale-100 opacity-100"
          } 
          ${disabled ? "cursor-not-allowed opacity-60" : ""}
          ${value?.id ? "border-none" : ""}

          `}
        >
          <div className="min-w-0 flex-1">
            {loading ? (
              <span className="px-4 text-sm text-text-secondary">
                Loading accounts...
              </span>
            ) : value ? (
              <CryptoAccountItem
                account={value}
                compact
              />
            ) : (
              <span className="px-4 text-sm text-text-secondary">
                {placeholder}
              </span>
            )}
          </div>

          <ChevronDownIcon
            size={20}
            className={`absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary custom-transition duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {/* Open/Search State */}
        <div
          className={`absolute inset-0 flex h-12 items-center gap-2 rounded-[10px] border border-divider bg-background-surface px-4 custom-transition duration-200 ${
            isOpen
              ? "scale-100 opacity-100"
              : "pointer-events-none scale-[0.98] opacity-0"
          }`}
        >
          <input
            ref={inputRef}
            autoFocus={isOpen}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => handleDropdownEnter(e, filteredAccounts, handleSelect)}
            placeholder="Search account..."
            disabled={loading}
            className="w-full border-0 bg-transparent p-0 text-sm text-text-primary placeholder:text-text-secondary outline-none focus:ring-0 disabled:opacity-60"
          />

          <button
            type="button"
            onClick={closeDropdown}
          >
            <ChevronDownIcon
              size={20}
              className="rotate-180 text-text-secondary custom-transition duration-200"
            />
          </button>
        </div>
      </div>

      <div
        className={`absolute left-0 right-0 top-[calc(100%+4px)] z-50 max-h-80 overflow-y-auto rounded-[8px] border border-divider bg-background-surface p-3 shadow-[0px_8px_16px_rgba(0,0,0,0.16)] custom-scrollbar origin-top custom-transition duration-200 ease-out ${
          isOpen
            ? "translate-y-0 scale-y-100 opacity-100 visible"
            : "-translate-y-2 scale-y-95 opacity-0 invisible pointer-events-none"
        }`}
      >
        <div
          className={`custom-transition duration-150 ${
            loading ? "opacity-70" : "opacity-100"
          }`}
        >
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, index) => (
                <CryptoAccountSkeleton key={index} />
              ))}
            </div>
          ) : filteredAccounts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <p className="text-sm font-medium text-text-primary">
                No accounts found
              </p>

              <p className="mt-1 text-xs text-text-secondary">
                {search
                  ? "Try another search term."
                  : "No crypto accounts are available."}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredAccounts.map((account, index) => (
                <div
                  key={account.id}
                  className="custom-transition duration-200"
                  style={{
                    transitionDelay: `${index * 30}ms`,
                    opacity: isOpen ? 1 : 0,
                    transform: isOpen
                      ? "translateY(0)"
                      : "translateY(-6px)",
                  }}
                >
                  <CryptoAccountItem
                    account={account}
                    selected={value?.id === account.id}
                    onClick={() => handleSelect(account)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};




// -------------------------------------------------------------------------------------------------------------

const AssetItem = ({ asset, selected = false, onClick }) => {
  const [assetImgError, setAssetImgError] = useState(false);
  const [networkImgError, setNetworkImgError] = useState(false);

  const networkLogo = asset.network?.logo_url;
  const assetName = asset.currency_short || asset.network.name || "AS";
  const assetSymbol = asset.name || asset.currency_name || "AS";
  const networkCode = asset.network?.code || "NW";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors duration-150 ${
        selected
          ? "bg-[rgba(74,185,50,0.10)]"
          : "hover:bg-[rgba(74,185,50,0.06)] bg-background-alt"
      }`}
    >
      {/* Avatar Container with Network Badge Overlay */}
      <div className="relative shrink-0">
        {/* Main Asset Image or Native Initial Avatar */}
        {asset.logo_url && !assetImgError ? (
          <img
            src={asset.logo_url}
            alt={assetSymbol}
            onError={() => setAssetImgError(true)}
            className="h-10 w-10 rounded-full object-contain bg-gray-100 p-0.5"
          />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-xs font-extrabold uppercase text-gray-700 select-none">
            {assetSymbol.slice(0, 3)}
          </div>
        )}

        {/* Small Network Logo Overlay on bottom-right of avatar */}
        {networkLogo && !networkImgError ? (
          <img
            src={networkLogo}
            alt={asset.network?.name}
            onError={() => setNetworkImgError(true)}
            className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full border-2 border-white bg-white object-contain"
          />
        ) : (
          <div className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-gray-300 text-[8px] font-bold uppercase text-gray-700 select-none">
            {networkCode.slice(0, 2)}
          </div>
        )}
      </div>

      {/* Main Info */}
      <div className="min-w-0 flex-1">
        {/* Title row: Code (Name) */}
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-bold text-text-primary">
            {assetName}
          </span>

          {assetSymbol && (
            <span className="truncate text-sm text-text-secondary font-normal">
              ({assetSymbol})
            </span>
          )}
        </div>

        {/* Subtitle row: Network Name */}
        <p className="truncate text-xs text-text-secondary mt-0.5">
          {asset.network?.name || "Unknown Network"}
        </p>
      </div>
    </button>
  );
};

const AssetSkeleton = () => (
  <div className="animate-pulse rounded-lg bg-background-alt p-4">
    <div className="flex items-center gap-3">
      <Skeleton className="h-10 w-10 rounded-full bg-divider" />

      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-28 rounded bg-divider" />
        <Skeleton className="h-3 w-20 rounded bg-divider" />
      </div>
    </div>
  </div>
);

export const AssetSelectDropdown = ({
  label,
  assets = [],
  value,
  onChange,
  placeholder = "Select Asset",
  showInfo = false,
  loading = false,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");

  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const closeDropdown = () => {
    setIsOpen(false);
    setSearch("");
  };

  useClickOutside(containerRef, isOpen, closeDropdown);

  const filteredAssets = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return assets;

    return assets.filter((asset) =>
      [
        asset?.asset_id,
        asset?.type,
      ]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(query))
    );
  }, [assets, search]);

  const handleSelect = (asset) => {
    onChange?.(asset);
    closeDropdown();
  };

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  return (
  <div
    ref={containerRef}
    className="relative flex w-full flex-col gap-3"
  >
    <FieldLabel showInfo={showInfo}>
      {label}
    </FieldLabel>

    {/* Trigger */}
    <div className="relative h-12">
      {/* Closed State */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(true)}
        className={`absolute inset-0 flex min-h-12 w-full items-center rounded-[10px] bg-background-surface text-left custom-transition duration-200 ${
          isOpen
            ? "pointer-events-none scale-[0.98] opacity-0"
            : "scale-100 opacity-100"
        } ${disabled ? "cursor-not-allowed opacity-60" : ""} ${!value ? "border border-divider" : ""}`}
      >
        <div className="min-w-0 flex-1">
          {loading ? (
            <span className="px-4 text-sm text-text-secondary">
              Loading assets...
            </span>
          ) : value ? (
            <AssetItem asset={value} compact />
          ) : (
            <span className="px-4 text-sm text-text-secondary">
              {placeholder}
            </span>
          )}
        </div>

        <ChevronDownIcon
          size={20}
          className={`absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary custom-transition duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Search State */}
      <div
        className={`absolute inset-0 flex h-12 items-center gap-2 rounded-[10px] border border-divider bg-background-surface px-4 custom-transition duration-200 ${
          isOpen
            ? "scale-100 opacity-100"
            : "pointer-events-none scale-[0.98] opacity-0"
        }`}
      >
        <input
          ref={inputRef}
          autoFocus={isOpen}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => handleDropdownEnter(e, filteredAssets, handleSelect)}
          placeholder="Search asset..."
          disabled={loading}
          className="w-full border-0 bg-transparent p-0 text-sm text-text-primary placeholder:text-text-secondary outline-none focus:ring-0 disabled:opacity-60"
        />

        <button
          type="button"
          onClick={closeDropdown}
        >
          <ChevronDownIcon
            size={20}
            className="rotate-180 text-text-secondary custom-transition duration-200"
          />
        </button>
      </div>
    </div>

    {/* Dropdown */}
    <div
      className={`absolute left-0 right-0 top-[calc(100%+4px)] z-50 max-h-80 overflow-y-auto overflow-hidden rounded-[8px] border border-divider bg-background-surface px-2 pb-3 py-2 shadow-[0px_8px_16px_rgba(0,0,0,0.16)] custom-scrollbar origin-top custom-transition duration-200 ease-out ${
        isOpen
          ? "visible translate-y-0 scale-y-100 opacity-100"
          : "pointer-events-none invisible -translate-y-2 scale-y-95 opacity-0"
      }`}
    >
      <div
        className={`custom-transition duration-150 ${
          loading ? "opacity-70" : "opacity-100"
        }`}
      >
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <AssetSkeleton key={index} />
            ))}
          </div>
        ) : filteredAssets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <p className="text-sm font-medium text-text-primary">
              No assets found
            </p>

            <p className="mt-1 text-xs text-text-secondary">
              {search
                ? "Try another search term."
                : "No assets are available."}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredAssets.map((asset) => (
              <AssetItem
                key={asset.id}
                asset={asset}
                selected={value?.id === asset.id}
                onClick={() => handleSelect(asset)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);
};




// -------------------------------------------------------------------------------------------------------------

const BeneficiaryItem = ({ beneficiary, labelName, isSelected }) => {
  // Determine beneficiary display name
  const beneficiaryName = beneficiary?.beneficiary_type === "INDIVIDUAL"
      ? `${beneficiary?.beneficiary_first_name ?? ""} ${beneficiary?.beneficiary_last_name ?? ""}`.trim()
      : beneficiary?.beneficiary_legal_name ?? "";

  // Derive primary avatar details (Asset)
  const name = beneficiaryName || beneficiary?.asset?.asset_id;
  // const logo = beneficiary?.logo_url;

  // Derive sub avatar details (Network)
  const subName = beneficiary?.network?.name || beneficiary?.network?.code || "";
  const subLogo = beneficiary?.network?.logo_url || beneficiary?.logo_url;

  const formatAddress = (addr) => {
    if (!addr) return "";
    return addr.length > 16 ? `${addr.slice(0, 8)}...${addr.slice(-6)}` : addr;
  };

  return (
    <button
      type="button"
      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors duration-150 ${
        isSelected
          ? "bg-[rgba(74,185,50,0.10)]"
          : "bg-background-alt hover:bg-[rgba(74,185,50,0.06)]"
      }`}
    >
      {/* Updated Common Avatar Component Usage */}
      <Avatar
        name={name}
        // logo={logo}
        subName={subName}
        subLogo={subLogo}
        subSizeClass="h-5 w-5 !text-[8px]"
      />

      {/* Beneficiary Details */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          {beneficiary?.network?.name && (
            <span className="shrink-0 text-xs font-semibold text-text-primary">
              {/* {beneficiaryName} ({beneficiary.network.code}) */}
              {beneficiaryName} ({beneficiary.network.name})
            </span>
          )}

          <p className="truncate text-sm font-bold text-text-primary">
            {labelName}
          </p>

          {beneficiary?.network?.code && (
            <span className="shrink-0 text-xs font-semibold text-text-secondary">
              {/* {beneficiary.network.name} */}
              {beneficiary.network.code}
            </span>
          )}
        </div>

        <p className="mt-0.5 truncate text-xs text-text-secondary">
          {formatAddress(beneficiary?.wallet_address)}
        </p>
      </div>
    </button>
  );
};

// Compact component specifically designed for the trigger input's h-12 constraints
const SelectedBeneficiary = ({ beneficiary, label }) => {
  const address = beneficiary?.wallet_address;
  const shortAddress = address ? `${address.slice(0, 6)}...${address.slice(-4)}` : "";

  // Determine beneficiary display name
  const beneficiaryName = beneficiary?.beneficiary_type === "INDIVIDUAL"
      ? `${beneficiary?.beneficiary_first_name ?? ""} ${beneficiary?.beneficiary_last_name ?? ""}`.trim()
      : beneficiary?.beneficiary_legal_name ?? "";

  // Derive primary avatar details (Asset)
  const name = beneficiaryName || beneficiary?.asset?.asset_id || "Asset";
  // const logo = beneficiary?.logo_url;

  // Derive sub avatar details (Network)
  const subName = beneficiary?.network?.name || beneficiary?.network?.code || "";
  const subLogo = beneficiary?.network?.logo_url ?? beneficiary?.logo_url;

  // const formatAddress = (addr) => {
  //   if (!addr) return "";
  //   return addr.length > 16 ? `${addr.slice(0, 8)}...${addr.slice(-6)}` : addr;
  // };

  return (
    <div className="flex min-w-0 items-center gap-3">
      {/* Updated Common Avatar Component */}
      <Avatar
        name={name}
        // logo={logo}
        subName={subName}
        subLogo={subLogo}
        sizeClass="h-8 w-8"
        subSizeClass="h-4 w-4 !text-[8px]"
      />

      <div className="flex flex-col min-w-0">
        {/* Label and Address */}
        <div className="flex items-center gap-2 min-w-0 truncate">
          <span className="flex-1 w-full truncate text-sm font-semibold text-text-primary">
            {/* {name} {beneficiary?.network?.code ? `(${beneficiary?.network?.code})` :""} */}
            {name} {beneficiary?.network?.name ? `(${beneficiary?.network?.name})` :""}
          </span>
          {shortAddress && (
            <span className="truncate text-xs text-text-secondary">
              ({shortAddress})
            </span>
          )}
        </div>
        <span className="truncate text-xs font-medium text-text-secondary">
          {beneficiary?.network?.code}
        </span>
      </div>
    </div>
  );
};

export const BeneficiaryDropdown = ({
  value,
  onChange,
  onAddBeneficiary,
  beneficiaries = [],
  loading = false,
  disabled = false,
  error = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");

  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Derive a user-friendly display name from beneficiary object properties
  const getBeneficiaryLabel = (beneficiary) => {
    if (!beneficiary) return "";
    if (beneficiary.label) return beneficiary.label;
    if (beneficiary.beneficiary_legal_name) return beneficiary.beneficiary_legal_name;
    if (beneficiary.beneficiary_first_name || beneficiary.beneficiary_last_name) {
      return `${beneficiary.beneficiary_first_name || ""} ${beneficiary.beneficiary_last_name || ""}`.trim();
    }
    return beneficiary.wallet_address
      ? `${beneficiary.wallet_address.slice(0, 6)}...${beneficiary.wallet_address.slice(-4)}`
      : "Unnamed Beneficiary";
  };

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearch("");
  }, []);

  // Handle outside clicks & key behaviors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && isOpen) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, closeDropdown]);

  // Search filter across label, wallet address, asset, and network parameters
  const filteredBeneficiaries = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return beneficiaries ?? [];

    return (beneficiaries ?? []).filter((b) => {
      const label = getBeneficiaryLabel(b);
      const address = b.wallet_address || "";
      const networkCode = b.network?.code || "";
      const networkName = b.network?.name || "";
      const assetCode = b.asset?.asset_id || "";

      return [label, address, networkCode, networkName, assetCode]
        .some((field) => field.toLowerCase().includes(query));
    });
  }, [beneficiaries, search]);

  const handleSelect = (beneficiary) => {
    if (disabled) return;
    onChange?.(beneficiary);
    closeDropdown();
  };

  return (
    <div ref={containerRef} className="relative flex flex-col gap-1.5 w-full">
      <FieldLabel>Beneficiary</FieldLabel>

      <div className="relative">
        {/* Trigger Bar */}
        <div className="relative h-12 w-full">
          {/* Closed Button State */}
          <button
            type="button"
            onClick={() => !disabled && setIsOpen(true)}
            disabled={disabled}
            className={`absolute inset-0 flex h-12 w-full items-center justify-between gap-2 rounded-[10px] border px-3.5 text-left custom-transition duration-200 ${
              isOpen ? "pointer-events-none scale-[0.98] opacity-0" : "scale-100 opacity-100"
            } ${disabled ? "cursor-not-allowed opacity-60" : ""} ${
              error ? "border-red-500" : "border-divider bg-background-surface"
            }`}
          >
            {/* Flex-1 and min-w-0 ensures children truncate instead of spilling over */}
            <div className="flex min-w-0 flex-1 items-center">
              {!loading ? (
                value ? (
                  <SelectedBeneficiary
                    beneficiary={value}
                    label={getBeneficiaryLabel(value)}
                  />
                ) : (
                  <span className="text-sm font-normal text-text-secondary">
                    Select beneficiary
                  </span>
                )
              ) : (
                <span className="text-sm font-normal text-text-secondary">
                  Loading beneficiaries...
                </span>
              )}
            </div>

            <ChevronIcon
              className={`h-4 w-4 shrink-0 text-text-secondary transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* Active Search Input */}
          <div
            className={`absolute inset-0 flex h-12 w-full items-center justify-between gap-2 rounded-[10px] border bg-background-surface px-4 custom-transition duration-200 ${
              isOpen ? "scale-100 opacity-100 z-10" : "pointer-events-none scale-[0.98] opacity-0"
            } ${error ? "border-red-500" : "border-divider"}`}
          >
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search beneficiary..."
              disabled={loading}
              className="w-full border-0 bg-transparent p-0 text-sm text-text-primary placeholder:text-text-secondary outline-none focus:ring-0 disabled:opacity-60"
            />

            <button
              type="button"
              onClick={closeDropdown}
              aria-label="Close beneficiary list"
              className="p-1 hover:opacity-75"
            >
              <ChevronIcon className="h-4 w-4 rotate-180 text-text-secondary" />
            </button>
          </div>
        </div>

        {/* Options Dropdown Menu */}
        <div
          className={`absolute left-0 right-0 top-[calc(100%+6px)] z-50 max-h-[280px] overflow-x-hidden 
            overflow-y-auto rounded-xl border border-divider bg-background-surface p-2 shadow-lg hide-scrollbar 
            origin-top custom-transition duration-200 ease-out ${
            isOpen
              ? "visible translate-y-0 scale-100 opacity-100"
              : "pointer-events-none invisible -translate-y-2 scale-95 opacity-0"
          }`}
        >
          {/* Add Beneficiary Action Button */}
          {onAddBeneficiary && (
            <button
              type="button"
              onClick={() => {
                onAddBeneficiary();
                closeDropdown();
              }}
              className="flex w-full items-center gap-2.5 rounded-lg bg-option-selected px-3 py-2.5 text-center text-sm font-medium text-[#4ab932] transition hover:bg-[#e8f4e3] mb-2"
            >
              <span className="flex size-5 items-center justify-center rounded-full border border-[#4ab932]">
                <PlusIcon />
              </span>
              Add Beneficiary
            </button>
          )}

          {/* List Content */}
          {loading ? (
            <div className="space-y-1.5 p-1">
              {Array.from({ length: 3 }).map((_, idx) => (
                <BeneficiarySkeleton key={idx} />
              ))}
            </div>
          ) : filteredBeneficiaries.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-6 py-6 text-center">
              <p className="text-sm font-medium text-text-primary">No beneficiaries found</p>
              <p className="mt-1 text-xs text-text-secondary">
                {search ? "Try another search term." : "Add your first beneficiary to continue."}
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredBeneficiaries.map((beneficiary) => {
                const isSelected = value?.id === beneficiary.id;
                const labelName = getBeneficiaryLabel(beneficiary);

                return (
                  <div key={beneficiary.id} onClick={() => handleSelect(beneficiary)}>
                    <BeneficiaryItem isSelected={isSelected} beneficiary={beneficiary} label={labelName} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {error && <p className="text-xs text-red-500 pl-1">{error}</p>}
    </div>
  );
};


// -------------------------------------------------------------------------------------------------------------


const NetworkItem = ({ asset, selected = false, onClick }) => {
  // Extract primary asset and secondary network avatar details
  const name = asset?.asset_id || "Asset";
  const logo = asset?.logo_url;
  const subName = asset?.network?.name || asset?.network?.code || "";
  const subLogo = asset?.network?.logo_url;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors duration-150 ${
        selected
          ? "bg-[rgba(74,185,50,0.10)]"
          : "bg-background-alt hover:bg-[rgba(74,185,50,0.06)]"
      }`}
    >
      {/* Refactored Common Avatar Component */}
      <Avatar
        name={name}
        logo={logo}
        subName={subName}
        subLogo={subLogo}
      />

      {/* Main Info */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-bold text-text-primary">
            {asset?.asset_id}
          </span>

          {asset?.network?.name && (
            <span className="truncate text-sm font-normal text-text-secondary">
              ({asset.network.name})
            </span>
          )}
        </div>

        <p className="mt-0.5 truncate text-xs text-text-secondary">
          {asset?.network?.name || "Unknown Network"}
        </p>
      </div>
    </button>
  );
};

const SelectedNetworkItem = ({ network }) => {
  // const address = network?.wallet_address;
  // const shortAddress = address
  //   ? `${address.slice(0, 6)}...${address.slice(-4)}`
  //   : "";

  // Extract primary asset and sub-network details
  const name = network?.asset_id || "Asset";
  const logo = network?.asset_logo || network.logo_url;
  const subName = network?.name || network?.code || "";
  const subLogo = network?.logo_url;

  return (
    <div className="flex min-w-0 items-center gap-3">
      {/* Refactored Avatar Component */}
      <Avatar
        name={name}
        logo={logo}
        subName={subName}
        subLogo={subLogo}
        sizeClass="h-8 w-8"
        subSizeClass="h-4 w-4 !text-[8px]"
      />

      <div className="flex flex-col min-w-0">
        {/* Label and Address */}
        <div className="flex items-center gap-2 min-w-0 truncate">
          <span className="truncate text-sm font-semibold text-text-primary">
            {/* {network?.currency_code} ({network?.name ?? network?.code}) */}
            {network?.currency_code} ({network?.name ?? network?.currency_name})
          </span>
        </div>
        <span className="truncate text-xs font-medium text-text-secondary">
          {network?.code}
        </span>
      </div>
    </div>
  );
};

// Skeleton Loader extracted outside component to avoid re-creation on re-renders
const NetworkSkeleton = () => (
  <div className="flex animate-pulse items-center gap-3 rounded-lg bg-background-alt px-3 py-2.5">
    <div className="h-10 w-10 shrink-0 rounded-full bg-divider" />
    <div className="flex-1 space-y-2">
      <div className="h-3.5 w-24 rounded bg-divider" />
      <div className="h-2.5 w-32 rounded bg-divider" />
    </div>
  </div>
);

export const NetworkDropdown = ({
  value,
  onChange,
  networks = [],
  loading = false,
  disabled = false,
  error = "",
  placeholder = "Select a network",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      if (listRef.current) {
        scrollIntoViewWithEase(listRef.current);
      }
    }
  }, [isOpen]);

  // Handle Close & Escape Key
  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearch("");
  }, []);

  // Handle outside click & key combinations
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && isOpen) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, closeDropdown]);

  // Filter networks based on search string
  const filteredNetworks = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return networks ?? [];

    return (networks ?? []).filter((network) => {
      const code = network?.code || network?.asset_id || "";
      const name = network?.name || network?.network?.name || "";
      return code.toLowerCase().includes(query) || name.toLowerCase().includes(query);
    });
  }, [networks, search]);

  const handleSelect = (network) => {
    if (disabled) return;
    onChange?.(network);
    closeDropdown();
  };

  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  };

  return (
    <div ref={containerRef} className="flex flex-col gap-1.5 w-full">
      <FieldLabel>Network</FieldLabel>

      <div className="relative">
        {/* Trigger Header */}
        <div className="relative h-16 w-full">
          {/* Closed Display Button */}
          <button
            type="button"
            onClick={toggleDropdown}
            disabled={disabled}
            className={`absolute inset-0 flex h-12 w-full items-center justify-between rounded-[10px] border px-4 text-left custom-transition duration-200 ${
              isOpen ? "pointer-events-none scale-[0.98] opacity-0" : "scale-100 opacity-100"
            } ${disabled ? "cursor-not-allowed opacity-60" : ""} ${
              error ? "border-red-500" : "border-divider bg-background-surface"
            }`}
          >
            <span className="text-sm text-text-secondary">
              {value ? (
                <SelectedNetworkItem network={value} />
              ) : (
                placeholder
              )}
            </span>

            {!disabled && <ChevronIcon
              open={isOpen}
              className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
            />}
          </button>

          {/* Search Bar Input (Active state) */}
          <div
            ref={listRef}
            className={`absolute inset-0 flex h-12 items-center gap-2 rounded-[10px] border bg-background-surface px-4 custom-transition duration-200 ${
              isOpen ? "scale-100 opacity-100 z-10" : "pointer-events-none scale-[0.98] opacity-0"
            } ${error ? "border-red-500" : "border-divider"}`}
          >
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search network..."
              disabled={loading}
              className="w-full border-0 bg-transparent p-0 text-sm text-text-primary placeholder:text-text-secondary outline-none focus:ring-0 disabled:opacity-60"
            />

            <button
              type="button"
              onClick={closeDropdown}
              aria-label="Close dropdown"
              className="p-1 hover:opacity-75"
            >
              <ChevronIcon open className="h-4 w-4 rotate-180 text-text-secondary" />
            </button>
          </div>
        </div>

        {/* Dropdown Options Container */}
        <div
          className={`absolute left-0 right-0 top-[calc(100%+6px)] z-50 max-h-[260px] overflow-y-auto rounded-xl border border-divider bg-background-surface p-2 shadow-lg hide-scrollbar origin-top custom-transition duration-200 ease-out ${
            isOpen
              ? "visible translate-y-0 scale-100 opacity-100"
              : "pointer-events-none invisible -translate-y-2 scale-95 opacity-0"
          }`}
        >
          {loading ? (
            <div className="space-y-1.5 p-1">
              {Array.from({ length: 3 }).map((_, idx) => (
                <NetworkSkeleton key={idx} />
              ))}
            </div>
          ) : filteredNetworks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <p className="text-sm font-medium text-text-primary">No networks found</p>
              <p className="mt-1 text-xs text-text-secondary">
                {search ? "Try another search term." : "No networks available."}
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredNetworks.map((network) => {
                const isSelected = value?.id === network.id || value?.code === network.code;
                return (
                  <NetworkItem
                    key={network.id || network.code}
                    asset={network}
                    selected={isSelected}
                    onClick={() => handleSelect(network)}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>

      {error && <p className="text-xs text-red-500 pl-1">{error}</p>}
    </div>
  );
};


// -------------------------------------------------------------------------------------------------------------


// Simple Selection Dropdown (For options like Beneficiary Type & Wallet Type)
export const SelectDropdown = ({
  label,
  value,
  onChange,
  options = [],
  disabled = false,
  error = "",
  placeholder = "Select an option",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (isOpen && listRef.current) {
      scrollIntoViewWithEase(listRef.current);
    }
  }, [isOpen]);

  useClickOutside(containerRef, isOpen, () => setIsOpen(false));

  const selectedOption = options.find((opt) => opt.value === value);

  const handleSelect = (optionValue) => {
    if (disabled) return;
    onChange?.(optionValue);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="flex flex-col gap-2">
      {label && (
        <label className="block text-sm font-medium text-text-primary">
          {label}
        </label>
      )}

      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen((prev) => !prev)}
          disabled={disabled}
          className={`flex h-12 w-full items-center justify-between rounded-[10px] border px-4 text-left custom-transition duration-200 ${
            disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
          } ${error ? "border-red-500 bg-error-500/10" : "border-divider bg-background-surface"}`}
        >
          <span className="text-sm">
            {selectedOption ? (
              <span className="font-medium text-text-primary">
                {selectedOption.label}
              </span>
            ) : (
              <span className="text-text-secondary">{placeholder}</span>
            )}
          </span>
          
          <ChevronDownIcon
            size={20}
            className={`shrink-0 text-text-secondary custom-transition ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        <div
          ref={listRef}
          className={`absolute left-0 right-0 top-[calc(100%+4px)] z-50 max-h-[220px] overflow-y-auto rounded-[12px] border border-divider bg-background-surface p-2 shadow-[0_8px_24px_rgba(0,0,0,0.08)] hide-scrollbar origin-top custom-transition duration-200 ease-out ${
            isOpen
              ? "visible translate-y-0 scale-y-100 opacity-100"
              : "pointer-events-none invisible -translate-y-2 scale-y-95 opacity-0"
          }`}
        >
          <div className="space-y-1">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={`flex w-full rounded-md px-4 py-2.5 text-left text-sm custom-transition ${
                  value === option.value
                    ? "bg-option-hover font-semibold text-text-primary"
                    : "bg-transparent text-text-secondary hover:bg-option-hover hover:text-text-primary"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};


// -------------------------------------------------------------------------------------------------------------

const VASPItem = ({ option, selected = false, onClick, isTrigger = false }) => {
  const [imgError, setImgError] = useState(false);

  const vaspLabel = option?.label || option?.name || "VASP";
  const vaspLogo = option?.logo;
  const vaspCountry = option?.country;
  const vaspWebsite = option?.website;

  // Render minimal layout inside the closed input trigger box
  if (isTrigger) {
    return (
      <div className="flex items-center gap-3 min-w-0 pr-2">
        <div className="relative shrink-0">
          {vaspLogo && !imgError ? (
            <img
              src={vaspLogo}
              alt={vaspLabel}
              onError={() => setImgError(true)}
              className="h-8 w-8 rounded-full object-contain bg-white p-0.5 shadow-sm border border-divider"
            />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-[10px] font-bold uppercase text-gray-700 select-none">
              {vaspLabel.slice(0, 2)}
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1 flex flex-col justify-center">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-bold text-text-primary">
              {vaspLabel}
            </span>
            {vaspCountry && (
              <span className="shrink-0 rounded bg-divider/60 px-1.5 py-0.5 text-[10px] font-semibold text-text-secondary uppercase">
                {vaspCountry}
              </span>
            )}
          </div>
          {vaspWebsite && (
            <p className="truncate text-[11px] text-text-secondary leading-tight">
              {vaspWebsite.replace(/^https?:\/\//, "")}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Standard Option Item for Dropdown List
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors duration-150 ${
        selected
          ? "bg-[rgba(74,185,50,0.10)]"
          : "hover:bg-[rgba(74,185,50,0.06)] bg-background-alt"
      }`}
    >
      <div className="relative shrink-0">
        {vaspLogo && !imgError ? (
          <img
            src={vaspLogo}
            alt={vaspLabel}
            onError={() => setImgError(true)}
            className="h-10 w-10 rounded-full object-contain bg-white p-0.5 shadow-sm border border-divider"
          />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-xs font-bold uppercase text-gray-700 select-none">
            {vaspLabel.slice(0, 2)}
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-bold text-text-primary">
            {vaspLabel}
          </span>
          {vaspCountry && (
            <span className="shrink-0 rounded bg-divider/60 px-1.5 py-0.5 text-[10px] font-semibold text-text-secondary uppercase">
              {vaspCountry}
            </span>
          )}
        </div>

        {vaspWebsite && (
          <p className="truncate text-xs text-text-secondary mt-0.5">
            {vaspWebsite.replace(/^https?:\/\//, "")}
          </p>
        )}
      </div>
    </button>
  );
};

export const VASPListDropdown = ({
  label,
  value,
  onChange,
  options = [],
  loading = false,
  disabled = false,
  error = "",
  placeholder = "Select VASP",
  searchPlaceholder = "Search VASP...",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const closeDropdown = () => {
    setIsOpen(false);
    setSearch("");
  };

  useClickOutside(containerRef, isOpen, closeDropdown);

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options ?? [];

    return (options ?? []).filter((opt) =>
      [opt?.label, opt?.value, opt?.code, opt?.name, opt?.website, opt?.country]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(query))
    );
  }, [options, search]);

  const handleSelect = (option) => {
    if (disabled) return;
    onChange?.(option);
    closeDropdown();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && filteredOptions.length > 0) {
      event.preventDefault();
      handleSelect(filteredOptions[0]);
    }
  };

  useEffect(() => {
    if (isOpen && listRef.current) {
      scrollIntoViewWithEase(listRef.current);
    }
  }, [isOpen]);

  const OptionSkeleton = () => (
    <div className="animate-pulse rounded-[6px] bg-background-alt px-4 py-3">
      <div className="h-4 w-32 rounded bg-divider" />
    </div>
  );

  return (
    <div ref={containerRef} className="flex flex-col gap-2">
      {label && (
        <label className="block text-sm font-medium text-text-primary">
          {label}
        </label>
      )}

      <div className="relative">
        <div className="relative h-14">
          {/* Trigger (Closed State) */}
          <button
            type="button"
            onClick={() => !disabled && setIsOpen((prev) => !prev)}
            disabled={disabled}
            className={`absolute inset-0 flex h-14 w-full items-center justify-between rounded-[12px] border px-3 text-left custom-transition duration-200 
              ${isOpen ? "pointer-events-none scale-[0.98] opacity-0" : "scale-100 opacity-100"} 
              ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"} 
              ${error ? "border-red-500 bg-error-500/10" : "border-divider bg-background-surface"}
            `}
          >
            {value ? (
              <VASPItem option={value} isTrigger />
            ) : (
              <span className="text-sm text-text-secondary truncate">
                {placeholder}
              </span>
            )}

            <ChevronDownIcon
              size={20}
              className={`shrink-0 text-text-secondary custom-transition ml-2 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* Search Input State */}
          <div
            ref={listRef}
            className={`absolute inset-0 flex h-14 items-center gap-2 rounded-[12px] border bg-background-surface px-4 custom-transition ${
              isOpen
                ? "scale-100 opacity-100"
                : "pointer-events-none scale-[0.98] opacity-0"
            } ${error ? "border-red-500 bg-error-500/10" : "border-divider"}`}
          >
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={searchPlaceholder}
              disabled={loading}
              className="w-full border-0 bg-transparent p-0 text-sm text-text-primary placeholder:text-text-secondary outline-none focus:ring-0 disabled:opacity-60"
            />

            <button
              type="button"
              onClick={closeDropdown}
              aria-label="Close options list"
              className="shrink-0 text-text-secondary hover:text-text-primary"
            >
              <ChevronIcon open />
            </button>
          </div>
        </div>

        {/* Dropdown Options List */}
        <div
          className={`absolute left-0 right-0 top-[calc(100%+6px)] z-40 max-h-[240px] overflow-y-auto rounded-[12px] border border-divider bg-background-surface p-2 shadow-[0_8px_24px_rgba(0,0,0,0.08)] hide-scrollbar origin-top custom-transition duration-200 ease-out ${
            isOpen
              ? "visible translate-y-0 scale-y-100 opacity-100"
              : "pointer-events-none invisible -translate-y-2 scale-y-95 opacity-0"
          }`}
        >
          {loading ? (
            <div className="space-y-2 p-1">
              {Array.from({ length: 4 }).map((_, index) => (
                <OptionSkeleton key={index} />
              ))}
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <p className="text-sm font-medium text-text-primary">
                No results found
              </p>
              <p className="mt-1 text-xs text-text-secondary">
                {search ? "Try another search term." : "No options available."}
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredOptions.map((option) => (
                <VASPItem
                  key={option.value || option.id}
                  option={option}
                  selected={
                    value?.value === option.value || value?.id === option.id
                  }
                  onClick={() => handleSelect(option)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};


// -------------------------------------------------------------------------------------------------------------